import { isNonMemberGuestMembershipType } from './membership';

/* ------------------------------------------------------------------
   Two people of the same name.

   A club's player list with two rows reading "Bob Smith" is no use to
   anybody: not on a draw sheet, not on a score card, not when an admin
   is trying to work out who has paid. Almost every time it happens it
   is one person entered twice rather than two people who genuinely
   share a name.

   Genuinely sharing one does happen, though, so nothing here refuses.
   It warns, and gives whoever is looking enough to decide — which is
   the part that is easy to get wrong. Asking "is this the same
   person?" while showing only the name is asking about the one thing
   that already matches, and invites a yes that means nothing.

   Lives here rather than in a screen because two of them ask now: the
   competition player list and the admin guest form.
   ------------------------------------------------------------------ */

/**
 * Club players already going by this name.
 *
 * Case and surrounding space are ignored — "bob smith" typed in a hurry is
 * the same person, and warning about one spelling but not the other would
 * make the check look arbitrary. Nothing cleverer: a fuzzy match would start
 * asking whether Rob is Bob, and "Bob A Smith" is exactly the answer this
 * check asks people to give.
 */
export function sameNamePlayers(users, name) {
  const wanted = String(name || '').trim().toLowerCase();
  if (!wanted) return [];
  return (users || []).filter(u =>
    String(u && u.name).trim().toLowerCase() === wanted);
}

/**
 * What there is to tell one player from another of the same name.
 *
 * An email is the strongest answer, because it belongs to one person. The
 * membership number is next. Whether they are a visitor rather than a member
 * often settles it on its own.
 *
 * Returns null when there is genuinely nothing to go on, which is worth
 * saying out loud rather than papering over: an admin guessing is how the
 * wrong Bob Smith ends up in the draw.
 */
export function describePlayer(u) {
  if (!u) return null;
  const bits = [];
  if (u.email) bits.push(u.email);
  if (u.membershipNumber) bits.push(`no. ${u.membershipNumber}`);
  if (isNonMemberGuestMembershipType(u.membershipType)) bits.push('a guest, not a member');
  return bits.length ? bits.join(' · ') : null;
}
